// Ported from case-desk src/theme/theme.ts (commit 1d18e32, tag architecture-before-das-platform-2026-07-31)
// per docs/architecture/das-web.md § 14 on 2026-08-18: the theme factory derives every MUI default from
// the token source; tenant brand colors enter only through this factory (das-web § 8.3/§ 8.4).
import { createTheme } from '@mui/material/styles';

import { designTokens, type BrandColorOverrides } from './tokens';

export function createDasWebTheme(brandColors: BrandColorOverrides = {}) {
  const color = { ...designTokens.color, ...brandColors };

  return createTheme({
    breakpoints: {
      values: {
        lg: designTokens.breakpoints.desktop,
        md: designTokens.breakpoints.tablet,
        sm: 600,
        xl: 1536,
        xs: 0,
      },
    },
    colorSchemes: {
      light: {
        palette: {
          action: {
            hover: color.neutralHover,
            selected: color.brandSoft,
          },
          background: {
            default: color.background,
            paper: color.paper,
          },
          divider: color.neutralBorder,
          error: {
            main: color.error,
          },
          info: {
            main: color.info,
          },
          primary: {
            contrastText: color.paper,
            dark: color.primaryHover,
            main: color.primary,
          },
          success: {
            main: color.success,
          },
          text: {
            primary: color.text,
            secondary: color.textSecondary,
          },
          warning: {
            main: color.warning,
          },
        },
      },
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          icon: {
            paddingBlock: 4,
          },
          message: {
            paddingBlock: 4,
          },
          root: {
            borderRadius: designTokens.radius.control,
            padding: '4px 12px',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            height: designTokens.layout.userAvatarSize,
            width: designTokens.layout.userAvatarSize,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          size: 'small',
        },
        styleOverrides: {
          root: {
            borderRadius: designTokens.radius.control,
            minHeight: designTokens.density.controlHeight,
            paddingBlock: 4,
            paddingInline: 12,
            textTransform: 'none',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          '#root, #storybook-root': {
            height: '100%',
          },
          body: {
            height: '100%',
            overflow: 'hidden',
          },
          html: {
            height: '100%',
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiIconButton: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.radius.control,
            minHeight: designTokens.density.navigationItemHeight,
            paddingBlock: 4,
            paddingInline: 8,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: color.link,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 32,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: designTokens.radius.large,
          },
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
          },
          root: {
            lineHeight: 1.35,
            paddingBlock: designTokens.density.gridCellPaddingBlock,
            paddingInline: designTokens.density.gridCellPaddingInline,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
      },
    },
    cssVariables: true,
    shape: {
      borderRadius: designTokens.radius.control,
    },
    spacing: 8,
    typography: {
      fontFamily: designTokens.typography.fontFamily,
      fontSize: designTokens.typography.fontSize,
      h2: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.25,
      },
      h4: {
        fontSize: '1.375rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
    },
  });
}

export const theme = createDasWebTheme();
